import { useEffect, useState } from 'react'
import { api } from 'core/api/ConnectionManager'
import { isExist, isNotEmptyArray } from 'core/utils/coreUtil'
import { MapCreator } from 'features/map/controllers/MapCreator'
import { SetPartialData } from '../__addGroup/displays__addGroup.local.methods'
import { licenseType, rateTypeRename } from 'core/utils/displayUtil'
import helpersDisplays from 'core/helpers/_digitalSignages'

export interface VirtualDeviceGroupOption {
    id: number
    name?: string
    nestedKey: string
}

export interface VirtualDeviceTimezoneOption {
    id: string
    name: string
    timezoneAbbr: string
    gmtOffset: number
}

export interface VirtualDeviceTagOption {
    id?: number
    name: string
    label: string
}

export interface VirtualDeviceLicenseData {
    id: number
    name: string
    vacantCount: number
    devicesCount: number
    expiredAt: string | null
    paidQ: boolean
    activeQ: boolean
}

export type VirtualDeviceLicenseOption = VirtualDeviceLicenseData | { id: null; name: string }

const mapFactory = MapCreator.getInstance().getMapFactory()
const mapApi = mapFactory.createMapApi()

export interface IAddVirtualDeviceProps {
    groups: VirtualDeviceGroupOption[]
    currentGroup?: { id: number }
    user?: any
    data?: { parentId: number | string; tags: VirtualDeviceTagOption[] }
    parentTimezone?: {
        timezone: number
    }
    setPartialData?: (data: SetPartialData) => void
    emitError?: (error: string) => void
    closeModal?: () => void
    lat?: number
    lng?: number
    currentLicense?: {
        activeQ: boolean
        expiredAt: null | string
        id: number
        name: string
        paidQ: boolean
        vacantCount: number
    } | null
    licensesData?: {
        id: number
        name: string
        vacantCount: number
        devicesCount: number
        expiredAt: string
        paidQ: boolean
        activeQ: boolean
    }[]
    timezones?: VirtualDeviceTimezoneOption[]
    tagsList?: VirtualDeviceTagOption[]
    label?: any
    tabid: string
}

type Data = {
    name?: string
    nullTimezoneError?: string | null
    licensesTypeError?: boolean | null
    error?: string | null
    address?: string
    tags?: VirtualDeviceTagOption[]
    groupScheduleMode?: string
    groupId?: number
    lat?: number
    lng?: number
    timezone?: string
    description?: string
    orientation?: string | number
    licenseId?: number | null
    DSNotificationsData?: { unitId: number | null; floorId: number | null } | null
    timezoneAbbr?: string
    timezoneOffset?: number
}

export interface IState {
    unitList: any
    validateSymbolNumber: boolean
    active: boolean
    mapCenter: { lat: number; lng: number } | null
    initialAddress: string
    data: Data
    groupTimezone: boolean
    parentTimezone: string | null
    licensesData: VirtualDeviceLicenseData[]
    currentLicense: VirtualDeviceLicenseOption | null
}

export const DisplaysAddVirtualDeviceMethods = (p_: IAddVirtualDeviceProps) => {
    const [state, setState] = useState<IState>({
        active: false,
        mapCenter: null,
        initialAddress: '',
        data: {
            name: '',
            address: '',
            tags: [],
            groupScheduleMode: 'apply',
            nullTimezoneError: null,
            licensesTypeError: false,
            error: null,
            licenseId: null,
        },
        groupTimezone: false,
        parentTimezone: null,
        licensesData: [],
        currentLicense: null,
        validateSymbolNumber: false,
        unitList: [],
    })

    useEffect(() => {
        const s_ = { ...state }

        if (p_.currentGroup) {
            s_.data.groupId = p_.currentGroup.id
        }

        api.send('getDsNotifications', {}).then((res: any) => {
            if (!res) return

            setState((prevState: IState) => ({
                ...prevState,
                unitList: res,
            }))
        })

        setState((prevState: IState) => ({
            ...prevState,
            ...s_,
        }))

        getLicenses(p_.user)
    }, [])

    useEffect(() => {
        if (state.groupTimezone && p_.data && p_.data.parentId) {
            // @ts-ignore
            const parentTimezone: any = p_.groups.find((group) => group.id === p_.data.parentId)

            if (parentTimezone && parentTimezone['0'] && parentTimezone['1'] && parentTimezone['2']) {
                setState((prevState) => ({
                    ...prevState,
                    parentTimezone: parentTimezone['0'].timezone,
                    groupTimezone: true,
                    data: {
                        ...prevState.data,
                        timezone: parentTimezone['0'].timezone,
                        timezoneOffset: parentTimezone['1'].timezoneOffset,
                        timezoneAbbr: parentTimezone['2'].timezoneAbbr,
                        nullTimezoneError: null,
                    },
                }))
            }
        }
    }, [state.groupTimezone])

    const save = () => {
        if (state.active) {
            return
        }

        if (!state.data.name) {
            setState((prevState: IState) => ({
                ...prevState,
                data: { ...prevState.data, error: 'fieldIsRequired' },
            }))
        }
        if (!state.data.timezone) {
            setState((prevState: IState) => ({
                ...prevState,
                data: { ...prevState.data, nullTimezoneError: 'fieldIsRequired' },
            }))
        }
        if (state.currentLicense === null && helpersDisplays.isCheckLicenseQ()) {
            setState((prevState: IState) => ({
                ...prevState,
                data: { ...prevState.data, licensesTypeError: true },
            }))
        }
        if (state.data.timezone) {
            setState((prevState: IState) => ({
                ...prevState,
                data: { ...prevState.data, nullTimezoneError: null },
            }))
        }
        if (state.data.name) {
            setState((prevState: IState) => ({
                ...prevState,
                data: { ...prevState.data, error: null },
            }))
        }
        if (state.currentLicense !== null) {
            setState((prevState: IState) => ({
                ...prevState,
                data: { ...prevState.data, licensesTypeError: false },
            }))
        }
        if (
            !state.data.name ||
            !state.data.timezone ||
            (state.currentLicense === null && helpersDisplays.isCheckLicenseQ())
        ) {
            return
        }

        let tags: string[] = []
        if (state.data.tags) {
            tags = state.data.tags.map((tag: { label: string }) => tag.label)
        }

        api.send('createDigitalSignage', { ...state.data, tags }).then(() => {
            closeModal()
        })
    }

    const getLicenses = (companyId: number) => {
        api.send('getLicenseByFilters', {
            sort: [['licenseId', 'ASC']],
            limit: 1000,
            companyId: companyId,
        }).then((res: any) => {
            let optionFormat = res.data
                .filter((data: any) => data.activeQ)
                .map((opt: any) => ({
                    id: opt.licenseId,
                    name: rateTypeRename(opt.rateType) + ' (' + licenseType(opt) + ')',
                    vacantCount: opt.vacantCount,
                    devicesCount: opt.devicesCount,
                    expiredAt: opt.expiredAt,
                    paidQ: opt.paidQ,
                    activeQ: opt.activeQ,
                }))
            setState((prevState: IState) => ({
                ...prevState,
                licensesData: optionFormat,
            }))
        })
    }

    const inputNullCheck = (value: string | null, type: string) => {
        if (type === 'name') {
            value && !/^\s*$/.test(value)
                ? setState((prevState: IState) => ({ ...prevState, data: { ...prevState.data, error: null } }))
                : setState((prevState: IState) => ({
                      ...prevState,
                      data: { ...prevState.data, error: 'fieldIsRequired' },
                  }))
        }
        if (type === 'timezone') {
            value
                ? setState((prevState: IState) => ({
                      ...prevState,
                      data: { ...prevState.data, nullTimezoneError: null },
                  }))
                : setState((prevState: IState) => ({
                      ...prevState,
                      data: { ...prevState.data, nullTimezoneError: 'fieldIsRequired' },
                  }))
        }
    }

    const setData = (newData: Data) => {
        setState((prevState: IState) => ({
            ...prevState,
            data: {
                ...state.data,
                ...newData,
            },
        }))
    }

    const closeModal = () => {
        const { closeModal } = p_

        setState((prevState: IState) => ({
            ...prevState,
            data: { ...prevState.data, nullTimezoneError: null, error: null, licensesTypeError: false },
        }))

        if (closeModal) {
            closeModal()
        }
    }

    const onChangeAddress = (address: string) => {
        setState((prevState: IState) => ({ ...prevState, data: { ...prevState.data, address } }))
    }

    const onChangeLocation = (location: { name: string; lat: number; lng: number }) => {
        const { name, lat, lng } = location

        setState((prevState: IState) => ({
            ...prevState,
            data: { ...prevState.data, address: name, lat, lng },
            initialAddress: name,
        }))
    }

    const onChangePlacemarkGeometry = (geometry: { lat: number; lng: number }) => {
        const { lat, lng } = geometry

        setState((prevState: IState) => ({ ...prevState, data: { ...prevState.data, lat, lng } }))
    }

    const updateLocation = () => {
        const { address } = state.data
        const { initialAddress } = state

        if (address === initialAddress) {
            save()

            return
        }

        mapApi
            .geocodeLocation(address)
            .then((mapLocationList) => {
                if (!isNotEmptyArray(mapLocationList)) {
                    save()
                    return
                }

                let { lat, lng }: any = mapLocationList[0]

                setState((prevState: IState) => ({ ...prevState, data: { ...prevState.data, lat, lng } }))

                setTimeout(save, 200)
            })
            .catch(() => {
                save()
            })
    }

    const onSave = () => {
        updateLocation()
    }

    const getAddress = () => {
        const { address } = state.data

        return address || ''
    }

    const getCoordinate = () => {
        const { lat, lng } = state.data

        return {
            lat: lat || null,
            lng: lng || null,
        }
    }

    const getLocation = () => {
        return {
            name: getAddress(),
            ...getCoordinate(),
        }
    }

    const getCenter = () => {
        const { mapCenter } = state

        if (isExist(mapCenter) && isExist(mapCenter!.lat) && isExist(mapCenter!.lng)) {
            return mapCenter
        }

        return null
    }

    const onMapClose = () => {
        const { lat, lng }: any = p_

        setState((prevState: IState) => ({
            ...prevState,
            active: false,
            mapCenter: {
                lat,
                lng,
            },
        }))
    }

    const onValidateSymbolNumber = (isValidate: boolean) => {
        setState((prevState: IState) => ({ ...prevState, validateSymbolNumber: isValidate }))
    }

    return {
        state,
        setState,
        onMapClose,
        getLocation,
        onChangePlacemarkGeometry,
        onChangeLocation,
        getCenter,
        onChangeAddress,
        getAddress,
        setData,
        onValidateSymbolNumber,
        closeModal,
        onSave,
        inputNullCheck,
    }
}
